import { useEffect, useState } from "react";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { fetchDeviceSharedVolumeChanges, type DeviceSharedVolumeChange } from "@/lib/api";
import {
	DEVICE_SHARED_VOLUME_CHANGE_LABELS,
	deviceSharedVolumeLocalPath,
	groupDeviceSharedVolumeTargets,
	type DeviceSharedVolumeTarget,
} from "@/lib/device-shared-volumes";
import { reportSharedVolumeFailure } from "@/lib/shared-volume-failure";
import { useDeviceSharedVolumeStore } from "@/stores/useDeviceSharedVolumeStore";

/** ダイアログに並べる変更の上限。 */
const CHANGE_LIST_LIMIT = 10;

/** 対象として名前を挙げる上限。 */
const TARGET_LIST_LIMIT = 3;

interface PushCounts {
	total: number;
	modified: number;
	added: number;
	deleted: number;
	unsupported: number;
	truncated: boolean;
	items: { volumeId: string; change: DeviceSharedVolumeChange }[];
	/** 数え直せず、最後に確かめたときの件数。 */
	stale: boolean;
}

type CountState =
	| { status: "loading" }
	| { status: "ready"; counts: PushCounts }
	| { status: "failed"; message: string };

/** 数え直してから、対象ごとの変更をまとめる。数え直せなければ、最後に確かめたときの件数にする。 */
async function countPushChanges(targets: readonly DeviceSharedVolumeTarget[]): Promise<PushCounts> {
	const store = useDeviceSharedVolumeStore.getState();
	const environmentId = store.environmentId;
	if (environmentId === null) throw new Error("環境が選ばれていません。");
	// 数え直せなければ知らせたうえで、最後に確かめたときの件数で続ける。
	const scan = store.refresh({ scan: true, relist: "none" });
	reportSharedVolumeFailure(scan, "反映の前に手元の変更を数え直せませんでした。");
	const scanned = await scan.then(
		(overview) => overview,
		() => null,
	);
	const stale = scanned === null || scanned.device.unavailableReason !== null;
	const counts: PushCounts = {
		total: 0,
		modified: 0,
		added: 0,
		deleted: 0,
		unsupported: 0,
		truncated: false,
		items: [],
		stale,
	};
	const scopes = [...groupDeviceSharedVolumeTargets(targets)].flatMap(([volumeId, paths]) =>
		(paths ?? [""]).map((path) => ({ volumeId, path })),
	);
	const fetched = await Promise.all(
		scopes.map(({ volumeId, path }) =>
			fetchDeviceSharedVolumeChanges(environmentId, volumeId, path, CHANGE_LIST_LIMIT),
		),
	);
	for (const [index, changes] of fetched.entries()) {
		const { volumeId } = scopes[index];
		counts.total += changes.total;
		counts.modified += changes.modified;
		counts.added += changes.added;
		counts.deleted += changes.deleted;
		counts.unsupported += changes.unsupported;
		counts.truncated ||= changes.truncated;
		counts.items.push(...changes.items.map((change) => ({ volumeId, change })));
	}
	return counts;
}

/** 対象を「パス」か「ボリューム「名前」全体」で挙げる。多ければ先頭だけにして残りの数を添える。 */
function describeTargets(targets: readonly DeviceSharedVolumeTarget[]): string {
	const volumes = useDeviceSharedVolumeStore.getState().overview?.volumes ?? [];
	const names = targets.map((target) => {
		const volume = volumes.find((candidate) => candidate.volumeId === target.volumeId);
		if (!volume) return target.path;
		return target.path === ""
			? `ボリューム「${volume.name}」全体`
			: deviceSharedVolumeLocalPath(volume, target.path);
	});
	const shown = names.slice(0, TARGET_LIST_LIMIT).join("、");
	return names.length > TARGET_LIST_LIMIT
		? `${shown} ほか ${names.length - TARGET_LIST_LIMIT} 件`
		: shown;
}

function describeCounts(counts: PushCounts): string {
	const base = `更新 ${counts.modified + counts.added} 件・削除 ${counts.deleted} 件`;
	const parts = [counts.stale ? `最後に確かめたときは ${base}` : base];
	if (counts.truncated) parts.push("(一部だけ数えました)");
	if (counts.unsupported > 0) parts.push(`。対象外 ${counts.unsupported} 件は反映しません`);
	return parts.join("");
}

function isSingleFile(targets: readonly DeviceSharedVolumeTarget[]): boolean {
	return targets.length === 1 && targets[0].entry?.kind === "file";
}

interface SharedVolumePushDialogProps {
	/** null なら閉じている。 */
	targets: DeviceSharedVolumeTarget[] | null;
	onPush: (targets: DeviceSharedVolumeTarget[]) => Promise<void>;
	onClose: () => void;
}

/**
 * 「共有ボリュームに反映…」の確認。手元の変更を数え直し、上書きされることと、対象・種類ごとの件数・変更の一覧を出す。
 * 反映する変更が無いと確かめられたら押せなくする。
 */
export function SharedVolumePushDialog({ targets, onPush, onClose }: SharedVolumePushDialogProps) {
	const [countState, setCountState] = useState<CountState>({ status: "loading" });

	useEffect(() => {
		if (targets === null) return;
		let cancelled = false;
		setCountState({ status: "loading" });
		countPushChanges(targets).then(
			(counts) => {
				if (!cancelled) setCountState({ status: "ready", counts });
			},
			(err: unknown) => {
				console.error("反映する変更を数えられませんでした。", err);
				if (!cancelled) {
					setCountState({
						status: "failed",
						message: err instanceof Error ? err.message : "反映する変更を数えられませんでした。",
					});
				}
			},
		);
		return () => {
			cancelled = true;
		};
	}, [targets]);

	const counts = countState.status === "ready" ? countState.counts : null;
	const nothingToPush = counts !== null && !counts.stale && !counts.truncated && counts.total === 0;
	const overview = useDeviceSharedVolumeStore((s) => s.overview);
	const volumes = overview?.volumes ?? [];

	return (
		<ConfirmDialog
			open={targets !== null}
			title="共有ボリュームに反映"
			message={
				targets !== null && isSingleFile(targets)
					? "コピーなので、このファイルを共有ボリュームへ反映すると上書きされます。"
					: "コピーなので、反映すると共有ボリュームの同じ名前のファイルが上書きされます。手元で消したファイルは共有ボリュームからも消えます。"
			}
			confirmLabel="反映"
			confirmVariant="primary"
			confirmDisabled={countState.status === "loading" || nothingToPush}
			onConfirm={async () => {
				if (targets === null) return;
				await onPush(targets);
				onClose();
			}}
			onCancel={onClose}
		>
			{targets !== null && (
				<div className="mt-3 flex flex-col gap-1.5 text-caption text-fg-2">
					<p className="m-0 break-all" data-testid="shared-volume-push-targets">
						対象: {describeTargets(targets)}
					</p>
					{countState.status === "loading" && (
						<p className="m-0 text-fg-3" data-testid="shared-volume-push-counting">
							反映する変更を数えています…
						</p>
					)}
					{countState.status === "failed" && (
						<p className="m-0" style={{ color: "var(--danger)" }}>
							{countState.message}
						</p>
					)}
					{counts !== null &&
						(nothingToPush ? (
							<p className="m-0 text-fg-1" data-testid="shared-volume-push-nothing">
								反映する変更はありません
							</p>
						) : (
							<p className="m-0 text-fg-1" data-testid="shared-volume-push-counts">
								{describeCounts(counts)}
							</p>
						))}
					{counts !== null && counts.items.length > 0 && (
						<ul
							className="m-0 flex flex-col gap-0.5 p-0"
							style={{ listStyle: "none" }}
							data-testid="shared-volume-push-changes"
						>
							{counts.items.slice(0, CHANGE_LIST_LIMIT).map(({ volumeId, change }) => {
								const volume = volumes.find((candidate) => candidate.volumeId === volumeId);
								const path = volume
									? deviceSharedVolumeLocalPath(volume, change.path)
									: change.path;
								return (
									<li key={`${volumeId}/${change.path}`} className="flex min-w-0 gap-2">
										<span className="shrink-0 text-fg-3">
											{DEVICE_SHARED_VOLUME_CHANGE_LABELS[change.kind]}
										</span>
										<span className="min-w-0 truncate" title={path} data-tooltip-if-truncated>
											{path}
										</span>
									</li>
								);
							})}
						</ul>
					)}
				</div>
			)}
		</ConfirmDialog>
	);
}
